import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import * as THREE from 'three';

import { gray2gradient, downloadCanvas, pointInConcavePolygon } from '../algorithms/geometry.js';
import Materials from '../algorithms/materials.js';
import { tic, toc } from '../algorithms/utils.js';
import {
  addShadingMediaCacheBuster,
  normalizeShadingMediaUrl
} from '../utils/mediaUrl.js';

const cvSelected = document.createElement('canvas');

const useMaterials = ({
  canvasPool,
  imgSrc,
  dsmTexture,
  month,
  setShadingTexture,
  sceneRef,
  fillPolygon,
  updateLoadingStatus,
  proposalShadings,
  shadingCacheKey = null,
  iframeCallback,
  onShadingLoaded,
  // External dependencies (passed from parent component like Canvas3D)
  lead = {},
  designId = null,
  user = {},
  saveShadingsAPI = { create: () => Promise.resolve() }
}) => {
  const handleSaveShadings = async (gradients) => {
    const { uid } = lead;
    const targetDesignId = designId || uid || null;
    const blob = new Blob([JSON.stringify(gradients)]);
    const formData = new FormData();
    formData.append('shadings', blob, 'shadings.json');
    formData.append('uid', uid);
    if (targetDesignId) {
      formData.append('design_id', targetDesignId);
      formData.append('canvas_id', targetDesignId);
    }
    const response = await saveShadingsAPI.create(formData);
    if (response) {
      await loadShadingJson(response, onShadingLoaded);
    }
    if (iframeCallback) {
      iframeCallback('Shading', 'Shading file upload completed');
    }
    return response;
  };

  const [{ rgbLoadStatus, shadingLoadStatus }, setLoadStatus] = useState({
    rgbLoadStatus: 'RGB.Loading',
    shadingLoadStatus: 'Shading.Loading'
  });

  const [rgbTexture, setRGBTexture] = useState();
  const [texture, setTexture] = useState();
  const [selectedTexture, setSelectedTexture] = useState();
  const [materials] = useState(new Materials(canvasPool));
  const shadingLoadRequestIdRef = useRef(0);
  const shadingLoadPromiseRef = useRef(Promise.resolve(null));
  const shadingLoadAbortControllerRef = useRef(null);

  useEffect(() => {
    materials.canvasPool = canvasPool;
    materials.fillPolygon = fillPolygon;
  }, [materials, canvasPool, fillPolygon]);

  const changeLoadStatus = (status) => {
    if (status.startsWith('RGB.')) setLoadStatus({ rgbLoadStatus: status, shadingLoadStatus });
    else if (status.startsWith('Shading.')) setLoadStatus({ rgbLoadStatus, shadingLoadStatus: status });
    updateLoadingStatus(status);
  };

  const changeMaterials = (scene) => {
    if (scene) {
      scene.traverse((node) => {
        if (node.material) {
          node.material.needsUpdate = true;
        }
      });
    }
  };

  const getSelectedTexture = (currentTexture) => {
    if (!currentTexture || !currentTexture.image || typeof currentTexture.image == 'object') return null;
    const sz = currentTexture.image.width;
    cvSelected.width = currentTexture.image.width;
    cvSelected.height = currentTexture.image.height;
    const ctx = cvSelected.getContext('2d');

    ctx.clearRect(0, 0, sz, sz);
    ctx.drawImage(currentTexture.image, 0, 0, cvSelected.width, cvSelected.height);

    const spacing = 6;
    const radius = 0.5;
    for (let y = 0; y < cvSelected.height; y += spacing) {
      for (let x = 0; x < cvSelected.width; x += spacing) {
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const txSelectedRoof = new THREE.CanvasTexture(cvSelected);
    txSelectedRoof.wrapS = txSelectedRoof.wrapT = THREE.RepeatWrapping;
    txSelectedRoof.repeat.set(1, 1);
    txSelectedRoof.needsUpdate = true;

    return txSelectedRoof;
  };

  const setCurrentTexture = (currentTexture, m = -1) => {
    setTexture(currentTexture);
    setSelectedTexture(getSelectedTexture(currentTexture));
    if (m !== -1) setShadingTexture(currentTexture, m);
    changeMaterials(sceneRef.current);
  };

  useEffect(() => {
    if (dsmTexture) dsmTexture.needsUpdate = true;
    changeMaterials(sceneRef.current);
  }, [dsmTexture, sceneRef]);

  const saveTextures = async (info, shadingData = null, diffuseData = null, dimensions = null) => {
    const data = materials.saveTextures(info, shadingData, diffuseData, dimensions);
    return await handleSaveShadings(data);
  };

  const loadShadingJson = (scr, callback = null, cacheKey = null) => {
    shadingLoadAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    shadingLoadAbortControllerRef.current = abortController;
    const requestId = shadingLoadRequestIdRef.current + 1;
    shadingLoadRequestIdRef.current = requestId;
    const src = normalizeShadingMediaUrl(scr);
    if (src) {
      const url = addShadingMediaCacheBuster(src);
      return fetch(url, { cache: 'no-store', signal: abortController.signal })
        .then((res) => {
          if (res.status === 404) return null;
          if (!res.ok) throw new Error(`Failed to load shading JSON: ${res.status}`);
          return res.json();
        })
        .then(async (json) => {
          if (requestId !== shadingLoadRequestIdRef.current) return;
          if (!json) {
            changeLoadStatus('Shading.None');
            return null;
          }
          try {
            const { diffuse } = json;
            if (!diffuse) throw Error();
            await materials.loadTextures(json);
            if (callback) callback(json);
            changeLoadStatus('Shading.Loaded');
            return json;
          } catch {
            iframeCallback?.('Error', { code: 'SHADING_LOAD_FAILED', message: 'Unable to load shading. Please try again.' });
            changeLoadStatus('Shading.Failed');
            return null;
          }
        })
        .catch((error) => {
          if (requestId !== shadingLoadRequestIdRef.current) return;
          if (error?.name === 'AbortError') return null;
          iframeCallback?.('Error', { code: 'SHADING_LOAD_FAILED', message: 'Unable to load shading. Please try again.' });
          changeLoadStatus('Shading.Failed');
          return null;
        })
        .finally(() => {
          if (shadingLoadAbortControllerRef.current === abortController) {
            shadingLoadAbortControllerRef.current = null;
          }
        });
    }
    materials.resetTSRFTextures();
    materials.resetTOFTextures();
    materials.resetSolarAccessTextures();
    if (rgbTexture) setCurrentTexture(rgbTexture);
    changeLoadStatus('Shading.None');
    return Promise.resolve(null);
  };

  useLayoutEffect(() => {
    if (!imgSrc) return;

    try {
      new THREE.TextureLoader().load(imgSrc, (tx) => {
        setRGBTexture(tx);
        setCurrentTexture(tx);
        changeLoadStatus('RGB.Loaded');
      });
    } catch {
      console.log('error in load texture');
      changeLoadStatus('RGB.Failed');
    }
  }, [imgSrc]);

  useEffect(() => {
    const scr = proposalShadings;
    const callback = (json) => {
      onShadingLoaded?.(json);
    };
    let timeoutId;
    let resolveLoad;
    shadingLoadPromiseRef.current = new Promise((resolve) => {
      resolveLoad = resolve;
      timeoutId = setTimeout(() => {
        loadShadingJson(scr, callback, shadingCacheKey).then(resolve);
      }, 300);
    });
    try {
      return () => {
        clearTimeout(timeoutId);
        shadingLoadRequestIdRef.current += 1;
        shadingLoadAbortControllerRef.current?.abort();
        shadingLoadAbortControllerRef.current = null;
        resolveLoad?.(null);
        shadingLoadPromiseRef.current = Promise.resolve(null);
      };
    } catch (err) {
      console.log(err);
    }
    return undefined;
  }, [proposalShadings, onShadingLoaded, shadingCacheKey]);

  const black = useMemo(
    () => (
      <meshStandardMaterial
        attach="material"
        color="rgb(0,0,0)"
        side={THREE.DoubleSide}
        blending={THREE.AdditiveBlending}
        castShadow
        receiveShadow
      />
    ),
    []
  );

  const groundSun = useMemo(
    () => (
      <meshStandardMaterial
        attach="material"
        color="#ffffff"
        wireframe={false}
        side={THREE.DoubleSide}
        map={texture}
        castShadow
        receiveShadow
      />
    ),
    [texture]
  );

  const ground = useMemo(
    () => <meshBasicMaterial attach="material" color="#ffffff" wireframe={false} side={THREE.DoubleSide} map={texture} />,
    [texture]
  );

  const groundDispSun = useMemo(
    () => (
      <meshStandardMaterial
        attach="material"
        color="#ffffff"
        wireframe={false}
        side={THREE.DoubleSide}
        map={texture}
        displacementMap={dsmTexture}
        displacementScale={100}
        castShadow
        receiveShadow
      />
    ),
    [texture, dsmTexture]
  );

  const groundDisp = useMemo(
    () => (
      <meshStandardMaterial
        attach="material"
        color="#000000"
        wireframe={false}
        side={THREE.DoubleSide}
        displacementMap={dsmTexture}
        displacementScale={100}
        emissive="#ffffff"
        emissiveMap={texture}
        metalness={1}
        roughness={1}
      />
    ),
    [texture, dsmTexture]
  );

  return {
    rgbTexture,
    setRGBTexture,
    texture,
    selectedTexture,
    setTexture,
    setCurrentTexture,
    changeMaterials,
    getAnualPanelsShadings: async (segments, panels, gwidth, gheight, options = {}) => {
      if (proposalShadings && options.waitForShadingLoad !== false) {
        await shadingLoadPromiseRef.current;
      }
      return materials.getAnualPanelsShadings(
        segments,
        panels,
        gwidth,
        gheight,
        options
      );
    },

    resetRGBTexture: () => {
      let randInt = Math.random();
      try {
        new THREE.TextureLoader().load(`${imgSrc}?${randInt}`, (tx) => {
          setRGBTexture(tx);
          setCurrentTexture(tx);
          changeLoadStatus('RGB.Loaded');
        });
      } catch {
        console.log('error in load texture');
        changeLoadStatus('RGB.Failed');
      }
    },

    resetTSRFTextures: materials.resetTSRFTextures,
    setTSRFTextures: materials.setTSRFTextures,
    getTSRFTexture: (m) => (m in materials.TSRFTextures ? materials.TSRFTextures[m] : null),

    resetTOFTextures: materials.resetTOFTextures,
    setTOFTextures: materials.setTOFTextures,
    getTOFTexture: (m) => (m in materials.TOFTextures ? materials.TOFTextures[m] : null),

    resetSolarAccessTextures: materials.resetSolarAccessTextures,
    setSolarAccessTextures: materials.setSolarAccessTextures,
    getSolarAccessTexture: (m) => (m in materials.SolarAccessTextures ? materials.SolarAccessTextures[m] : null),

    getDiffuseTextures: () => [
      materials.diffuseTextures.tsrf_iso,
      materials.diffuseTextures.tof_iso,
      materials.diffuseTextures.tsrf_horizon,
      materials.diffuseTextures.tof_horizon
    ],
    setDiffuseTextures: (diffuseTSRF_iso, diffuseTOF_iso, diffuseTSRF_horizon, diffuseTOF_horizon) => {
      materials.diffuseTextures.tsrf_iso = diffuseTSRF_iso;
      materials.diffuseTextures.tof_iso = diffuseTOF_iso;
      materials.diffuseTextures.tsrf_horizon = diffuseTSRF_horizon;
      materials.diffuseTextures.tof_horizon = diffuseTOF_horizon;
    },

    black,
    groundSun,
    ground,
    groundDispSun,
    groundDisp,

    loadShadingJson,
    saveTextures,
    beginShadingTextureJob: materials.beginShadingTextureJob,
    getMaskCanvas: materials.getMaskCanvas,
    gradiantData: materials.gradiantData
  };
};

export default useMaterials;
